import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Typography, Theme } from '@mui/material';

import { useAgentContext } from './ScoreExplanationTable.agentContext';

const EXPLANATION_STRINGS = appStrings.modules.lifeReadiness;

const InfoRow = ({ index, title, text }: { index: number; title: string; text: string }) => {
  return (
    <Box sx={infoRowContainerSx}>
      <Typography variant="text-sm-medium" sx={numberSx}>
        {index + 1}
      </Typography>

      <Typography variant="text-sm-bold" sx={titleSx}>
        {title}
      </Typography>

      <Typography variant="text-sm-regular" sx={descriptionSx}>
        {text}
      </Typography>
    </Box>
  );
};

export const ScoreExplanationTable = () => {
  useAgentContext();
  const getExplanations = () => {
    return EXPLANATION_STRINGS.questionTooltips.consolidated;
  };

  const explanations = getExplanations();

  return (
    <Box sx={containerSx}>
      <Box sx={headerContainerSx}>
        <Typography
          variant="text-xl-semibold"
          color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
        >
          {EXPLANATION_STRINGS.keyTitle}
        </Typography>
        <Typography
          variant="text-sm-regular"
          color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
        >
          {EXPLANATION_STRINGS.keyDescription}
        </Typography>
      </Box>

      <Box sx={explanationsContainerSx}>
        {Object.keys(explanations).map((explanation, index) => {
          const explanationData = explanations[explanation as keyof typeof explanations];

          return (
            <InfoRow
              key={explanation}
              index={index}
              title={explanationData.title}
              text={explanationData.tooltip}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  paddingX: backrs.spacing['spacing-xl'],
  paddingY: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-xl'],
});

const explanationsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const infoRowContainerSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  display: 'grid',
  gridTemplateColumns: '16px 70px 1fr',
  alignItems: 'start',
  gap: backrs.spacing['spacing-md'],
  [breakpoints.down('sm')]: {
    gridTemplateColumns: '16px 1fr',
    gridTemplateAreas: `
      "number title"
      ".      desc"
    `,
  },
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  mb: backrs.spacing['spacing-2xl'],
  flexWrap: 'wrap',
});

const numberSx: SxProps<Theme> = ({ backrs }) => ({
  gridArea: { xs: 'number', sm: 'auto' },
  color: backrs.colors['Colors/Text/text-secondary'],
  userSelect: 'none',
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  width: 'fit-content',
  whiteSpace: 'nowrap',
  gridArea: { xs: 'title', sm: 'auto' },
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
  userSelect: 'none',
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  gridArea: { xs: 'desc', sm: 'auto' },
  color: backrs.colors['Colors/Text/text-secondary-hover'],
});
